import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMode } from '@/contexts/ContextModeContext';
import PhaseScopeSelector from '../ui/PhaseScopeSelector';
import { useScope } from '@/contexts/ScopeContext';
import { useSchool } from '@/contexts/SchoolContext';
import { useDomainStore } from '@/stores/domainStore';
import DomainRegistryNotice from '@/features/domains/components/DomainRegistryNotice';
import { useRoleSwitcher } from '@/features/auth/hooks/useRoleSwitcher';
import { useAuth } from '@/features/auth/hooks/useAuth.jsx';
import { deleteDomain, getDomainEventCount } from '@/services/domainRegistry';
import { Button, ConfirmDialog } from '@/components/ui';
import { getPersistentContextBarPositionClass } from './persistentContextBarLayout';
import { domainSupportsSurface } from '@/services/domain/centralModel';
import { applyDomainSelection, resolveDomainSelection } from '@/services/domain/domainSelection';

const MODE_CONFIG = [
  { value: 'recognition', label: 'Recognitions' },
];

const DROPDOWN_GAP = 8;
const VIEWPORT_PADDING = 12;
const BEHAVIOUR_DROPDOWN_ID = '__behaviour__';

const normalizeDomainKey = (domain) =>
  `${domain?.id || ''} ${domain?.name || ''} ${domain?.label || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

const isPoliciesDomain = (domain) => normalizeDomainKey(domain).includes('polic');

/**
 * Routes whose pages consume `activeDomain` / `mode` from this bar. Pages NOT
 * in this list ignore the context selection, so the bar would mislead users
 * into thinking it filters something when it doesn't (issue #127).
 *
 * To opt a new page in, add its path here. Prefix-match — `/discipline` will
 * match `/discipline/queue` and `/discipline-rules`. Be specific.
 */
const DOMAIN_AWARE_ROUTES = [
  '/quick-issue',
  '/discipline/queue',
  '/discipline-rules',
  '/discipline-dashboard',
  '/records',
  '/rules',
  '/teacher-dashboard',
];

const isDomainAwareRoute = (pathname) =>
  DOMAIN_AWARE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const surfaceForRoute = (pathname) => {
  if (pathname === '/quick-issue' || pathname.startsWith('/quick-issue/')) return 'quickIssue';
  if (pathname === '/discipline/queue' || pathname.startsWith('/discipline/queue/')) return 'queue';
  if (pathname === '/records' || pathname.startsWith('/records/')) return 'records';
  if (pathname === '/rules' || pathname.startsWith('/rules/')
    || pathname === '/discipline-rules' || pathname.startsWith('/discipline-rules/')) return 'rules';
  if (pathname === '/teacher-dashboard' || pathname.startsWith('/teacher-dashboard/')) return 'quickIssue';
  if (pathname === '/discipline-dashboard' || pathname.startsWith('/discipline-dashboard/')) return 'dashboard';
  return null;
};

/**
 * PersistentContextBar — sticky strip rendered once in AppLayout.
 *
 * Mode pills: Behaviour menu · Recognitions
 * Domain pills: top-level non-protected active domains (from domainStore)
 *   - Domains with subdomains show a chevron; clicking opens a dropdown of subdomains.
 *   - Domains without subdomains toggle activation directly.
 * Scope picker: one choice for school, phase or organisational responsibilities
 *
 * Self-hides on routes not in DOMAIN_AWARE_ROUTES.
 */
const PersistentContextBar = ({ containedDesktop = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, setMode } = useMode();
  const { userGrades } = useScope();
  const { schoolId } = useSchool();
  const { domains, activeDomain, isLoaded: domainsLoaded, loadError, registryError, registryLoading, retryRegistry, setActiveDomain } = useDomainStore();
  const { currentRole } = useRoleSwitcher();
  const { user } = useAuth();
  const [expandedParentId, setExpandedParentId] = useState(null);
  const [archivingSubId, setArchivingSubId] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // { sub, count }
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const contextScrollerRef = useRef(null);
  const domainsGroupRef = useRef(null);
  const dropdownRef = useRef(null);
  const domainAnchorRefs = useRef(new Map());

  const isDomainMode = !!activeDomain;
  const isAdminOrDev = currentRole === 'admin' || currentRole === 'dev';
  const activeSurface = surfaceForRoute(location.pathname);
  const selectDomain = useCallback(domain => applyDomainSelection(domain, { schoolId, setMode, setActiveDomain }), [schoolId, setMode, setActiveDomain]);

  // Older record links persisted a mode-backed Domain as a generic form.
  // Restore the same selection as the header before the user records again.
  useEffect(() => {
    if (activeDomain && resolveDomainSelection(activeDomain).mode) selectDomain(activeDomain);
  }, [activeDomain, selectDomain]);
  const supportsActiveSurface = (domain) => !activeSurface
    || domainSupportsSurface(domain, activeSurface);

  // Visibility for non-admin: a domain shows if its scope.grades intersects
  // the user's grades, OR the user created it. Top-level domains with no
  // explicit scope stay broadcast (rare, usually admin-managed). Sub-domains
  // are typically created ad-hoc by teachers — a teacher creating
  // "Maths Olympiad" under their Grade-4 scope should not flood every other
  // teacher's dropdown, so sub-domains require an explicit match.
  const isVisibleToMe = (d, { isSub }) => {
    if (isAdminOrDev) return true;
    if (d.createdBy && user?.uid && d.createdBy === user.uid) return true;
    const scopeGrades = d.scope?.grades;
    if (!scopeGrades?.length) return !isSub;
    return scopeGrades.some(g => userGrades.has(g));
  };

  // Discipline + recognition are already represented by the Behaviour /
  // Recognitions controls above. Incident Reports appears inside the Behaviour
  // menu. Every other Domain is shown only where its central surface contract
  // says it works.
  const HIDDEN_FROM_DOMAIN_BAR = new Set([
    'discipline',
    'recognition',
    'incident-reports',
  ]);

  const visibleDomains = domains.filter(d => {
    if (d.active === false || d.parentDomainId) return false;
    if (HIDDEN_FROM_DOMAIN_BAR.has(d.name)) return false;
    const hasSupportedChild = domains.some((child) => (
      child.active !== false
      && child.parentDomainId === d.id
      && supportsActiveSurface(child)
      && (!isPoliciesDomain(child) || isAdminOrDev)
      && isVisibleToMe(child, { isSub: true })
    ));
    if (!supportsActiveSurface(d) && !hasSupportedChild) return false;
    // Library-style domains have no teacher logging surface. Keep the Policies
    // shortcut available to admins, who use it to reach the management page.
    if ((d.capabilities?.policyLibrary?.enabled || isPoliciesDomain(d)) && !isAdminOrDev) return false;
    return isVisibleToMe(d, { isSub: false });
  });

  const subdomainsOf = (parentId) =>
    domains.filter(d =>
      d.active !== false &&
      d.parentDomainId === parentId &&
      supportsActiveSurface(d) &&
      (!isPoliciesDomain(d) || isAdminOrDev) &&
      isVisibleToMe(d, { isSub: true })
    );

  const incidentReportsDomain = domains.find(d =>
    d.active !== false &&
    d.id === 'incident-reports' &&
    supportsActiveSurface(d) &&
    isVisibleToMe(d, { isSub: false })
  );

  const behaviourOptions = [
    { id: 'mentions', label: 'Mentions', kind: 'mode', mode: 'mention' },
    ...(incidentReportsDomain
      ? [{ id: 'incident-reports', label: 'Incident Reports', kind: 'domain', domain: incidentReportsDomain }]
      : []),
  ];
  const isBehaviourActive = activeDomain?.id === 'incident-reports' || (!isDomainMode && mode === 'mention');
  const behaviourLabel = activeDomain?.id === 'incident-reports' ? 'Incident Reports' : 'Mentions';

  const closeDomainDropdown = useCallback(() => {
    setExpandedParentId(null);
    setDropdownPosition(null);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!expandedParentId || typeof window === 'undefined') return;

    const anchor = domainAnchorRefs.current.get(expandedParentId);
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const dropdownRect = dropdownRef.current?.getBoundingClientRect();
    const dropdownWidth = dropdownRect?.width || 0;
    const dropdownHeight = dropdownRect?.height || 0;

    let left = anchorRect.left + (anchorRect.width / 2);
    let top = anchorRect.bottom + DROPDOWN_GAP;

    if (dropdownWidth > 0) {
      const halfWidth = dropdownWidth / 2;
      left = Math.max(
        VIEWPORT_PADDING + halfWidth,
        Math.min(window.innerWidth - VIEWPORT_PADDING - halfWidth, left)
      );
    }

    if (
      dropdownHeight > 0 &&
      top + dropdownHeight > window.innerHeight - VIEWPORT_PADDING
    ) {
      const topAbove = anchorRect.top - dropdownHeight - DROPDOWN_GAP;
      top = topAbove >= VIEWPORT_PADDING
        ? topAbove
        : Math.max(VIEWPORT_PADDING, window.innerHeight - VIEWPORT_PADDING - dropdownHeight);
    }

    setDropdownPosition((current) => {
      if (current?.top === top && current?.left === left) return current;
      return { top, left };
    });
  }, [expandedParentId]);

  useEffect(() => {
    if (!expandedParentId) return;
    const handler = (e) => {
      const clickedDomainBar = contextScrollerRef.current?.contains(e.target);
      const clickedDropdown = dropdownRef.current?.contains(e.target);
      if (!clickedDomainBar && !clickedDropdown) closeDomainDropdown();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expandedParentId, closeDomainDropdown]);

  useEffect(() => {
    if (!expandedParentId) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeDomainDropdown();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [expandedParentId, closeDomainDropdown]);

  useLayoutEffect(() => {
    if (!expandedParentId) return;

    updateDropdownPosition();
    const handleViewportChange = () => updateDropdownPosition();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateDropdownPosition);
      const anchor = domainAnchorRefs.current.get(expandedParentId);
      if (anchor) resizeObserver.observe(anchor);
      if (dropdownRef.current) resizeObserver.observe(dropdownRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      resizeObserver?.disconnect();
    };
  }, [expandedParentId, updateDropdownPosition]);

  // Keep the selected mode/domain reachable when the pill row is wider than a
  // narrow viewport. Scroll padding leaves a gutter around the selected pill.
  // Observe layout changes, but let people browse the row without snapping back.
  useEffect(() => {
    const scroller = contextScrollerRef.current;
    if (!scroller) return undefined;
    const revealSelected = () => scroller.querySelector(
      '[role="tab"][aria-selected="true"]',
    )?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    revealSelected();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(revealSelected);
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    return () => observer.disconnect();
  }, [mode, activeDomain?.id, location.pathname, domainsLoaded]);

  useEffect(() => {
    if (activeDomain && activeSurface && !domainSupportsSurface(activeDomain, activeSurface)) {
      setActiveDomain(null, schoolId);
    }
  }, [activeDomain, activeSurface, schoolId, setActiveDomain]);

  if (!isDomainAwareRoute(location.pathname)) return null;

  // In the split workspace the bar is a dedicated flex row above the Queue's
  // bounded scroller. It must participate in normal layout; sticky positioning
  // can paint it over the first part of that sibling scroller. The ordinary
  // document layout still needs a sticky origin below the fixed navbar.
  const positionClass = getPersistentContextBarPositionClass(containedDesktop);

  if (!domainsLoaded) {
    return (
      <div
        data-testid="persistent-context-bar-loading"
        role="toolbar"
        aria-label="Mode and scope"
        aria-busy="true"
        className={`${positionClass} z-40 px-3 pt-2 pb-2 sm:px-4 sm:pt-4 sm:pb-3`}
      >
        <div className="h-12" aria-hidden="true" />
      </div>
    );
  }

  const handleDomainClick = (domain) => {
    const subs = subdomainsOf(domain.id);
    if (subs.length > 0) {
      const next = expandedParentId === domain.id ? null : domain.id;
      setExpandedParentId(next);
      setDropdownPosition(null);
    } else {
      const isActive = activeDomain?.id === domain.id;
      selectDomain(isActive ? null : domain);
      setExpandedParentId(null);
    }
  };

  const handleBehaviourClick = () => {
    const next = expandedParentId === BEHAVIOUR_DROPDOWN_ID ? null : BEHAVIOUR_DROPDOWN_ID;
    setExpandedParentId(next);
    setDropdownPosition(null);
  };

  const handleBehaviourOptionClick = (option) => {
    if (option.kind === 'mode') {
      selectDomain({ id: option.mode === 'recognition' ? 'recognition' : 'discipline' });
    } else if (option.domain) {
      setMode('mention');
      selectDomain(option.domain);
    }
    closeDomainDropdown();
  };

  const handleSubdomainClick = (sub) => {
    const isActive = activeDomain?.id === sub.id;
    selectDomain(isActive ? null : sub);
    closeDomainDropdown();
  };

  const handleCreateSubdomain = (parent) => {
    closeDomainDropdown();
    navigate(`/domains?create=1&parent=${encodeURIComponent(parent.id)}`);
  };

  const handleArchiveSub = async (e, sub) => {
    e.stopPropagation();
    if (!schoolId || !isAdminOrDev) return;
    setArchivingSubId(sub.id);
    const count = await getDomainEventCount(schoolId, sub).catch(() => 0);
    setArchivingSubId(null);
    setArchiveTarget({ sub, count });
  };

  const performArchive = async () => {
    if (!archiveTarget) return;
    const { sub } = archiveTarget;
    setArchivingSubId(sub.id);
    try {
      await deleteDomain(schoolId, sub.id);
      if (activeDomain?.id === sub.id) setActiveDomain(null, schoolId);
    } catch (err) {
      console.error('[PersistentContextBar] deleteDomain failed:', err);
    } finally {
      setArchivingSubId(null);
    }
  };

  return (
    <div
      data-testid="persistent-context-bar"
      role="toolbar"
      aria-label="Mode and scope"
      className={`${positionClass} z-40 bg-base-200 px-3 pt-2 pb-2 sm:px-4 sm:pt-4 sm:pb-3`}
    >
      <DomainRegistryNotice />
      {loadError && !registryError && !registryLoading && (
        <div role="alert" className="mb-2 flex flex-wrap items-center justify-center gap-2 text-sm text-base-content">
          <span>{loadError}</span>
          <button type="button" className="btn btn-ghost btn-xs" onClick={retryRegistry || (() => window.location.reload())}>
            Try again
          </button>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        {/* Mode + Domain pills — one island */}
        <div
          ref={contextScrollerRef}
          data-testid="context-scroll-region"
          className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain scroll-px-4 px-3 py-1"
        >
          <div className="flex w-max items-center gap-1 bg-base-100 rounded-full shadow-sm border border-base-300 px-1.5 py-1">
          <div role="tablist" aria-label="Mode" className="flex items-center gap-1">
            <div
              ref={(node) => {
                if (node) domainAnchorRefs.current.set(BEHAVIOUR_DROPDOWN_ID, node);
                else domainAnchorRefs.current.delete(BEHAVIOUR_DROPDOWN_ID);
              }}
              className="relative"
            >
              <Button
                role="tab"
                aria-selected={isBehaviourActive}
                aria-expanded={expandedParentId === BEHAVIOUR_DROPDOWN_ID}
                aria-haspopup="listbox"
                aria-controls="behaviour-dropdown"
                className={`btn btn-xs min-h-0 h-7 !rounded-full border-0 shadow-none gap-1 ${isBehaviourActive || expandedParentId === BEHAVIOUR_DROPDOWN_ID ? 'btn-primary' : 'btn-ghost'}`}
                onClick={handleBehaviourClick}
              >
                {behaviourLabel}
                <ChevronDownIcon
                  className={`w-3 h-3 transition-transform duration-150 ${expandedParentId === BEHAVIOUR_DROPDOWN_ID ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </Button>
            </div>

            {MODE_CONFIG.map((m) => (
              <Button
                key={m.value}
                role="tab"
                aria-selected={!isDomainMode && mode === m.value}
                className={`btn btn-xs min-h-0 h-7 !rounded-full border-0 shadow-none ${!isDomainMode && mode === m.value ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => {
                  selectDomain({ id: m.value });
                  setExpandedParentId(null);
                }}
              >
                {m.label}
              </Button>
            ))}
          </div>

          {expandedParentId === BEHAVIOUR_DROPDOWN_ID && typeof document !== 'undefined' && createPortal(
            <div
              ref={dropdownRef}
              data-testid="behaviour-dropdown"
              className={`fixed z-[9999] min-w-max max-h-[calc(100vh-1.5rem)] overflow-y-auto bg-base-100 rounded-2xl shadow-lg border border-base-300 p-1 -translate-x-1/2 ${dropdownPosition ? '' : 'invisible'}`}
              style={{
                top: dropdownPosition?.top ?? 0,
                left: dropdownPosition?.left ?? 0,
              }}
            >
              <div
                id="behaviour-dropdown"
                role="listbox"
                aria-label="Behaviour forms"
                className="flex flex-col gap-0.5"
              >
                {behaviourOptions.map((option) => {
                  const isSelected = option.id === 'mentions'
                    ? !isDomainMode && mode === 'mention'
                    : activeDomain?.id === option.id;
                  return (
                    <Button
                      key={option.id}
                      role="option"
                      aria-selected={isSelected}
                      className={`btn btn-xs min-h-0 h-7 !rounded-full border-0 shadow-none justify-start gap-1 ${isSelected ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => handleBehaviourOptionClick(option)}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            </div>,
            document.body
          )}

          {visibleDomains.length > 0 && (
            <>
              <div className="h-5 w-px bg-base-300 mx-1" aria-hidden="true" />
              <div
                ref={domainsGroupRef}
                role="tablist"
                aria-label="Domains"
                className="flex items-center gap-1"
              >
                {visibleDomains.map((domain) => {
                  const subs = subdomainsOf(domain.id);
                  const hasSubs = subs.length > 0;
                  const isExpanded = expandedParentId === domain.id;
                  const isChildActive = hasSubs && activeDomain?.parentDomainId === domain.id;
                  const isDirectActive = !hasSubs && activeDomain?.id === domain.id;
                  const isHighlighted = isChildActive || isDirectActive;

                  return (
                    <div
                      key={domain.id}
                      ref={(node) => {
                        if (node) domainAnchorRefs.current.set(domain.id, node);
                        else domainAnchorRefs.current.delete(domain.id);
                      }}
                      className="relative"
                    >
                      <Button
                        role="tab"
                        aria-selected={isHighlighted}
                        aria-expanded={hasSubs ? isExpanded : undefined}
                        aria-haspopup={hasSubs ? 'listbox' : undefined}
                        aria-controls={hasSubs ? `domain-dropdown-${domain.id}` : undefined}
                        className={`btn btn-xs min-h-0 h-7 !rounded-full border-0 shadow-none gap-1 ${isHighlighted || isExpanded ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => handleDomainClick(domain)}
                      >
                        {isChildActive ? (
                          <>
                            {activeDomain.label}
                          </>
                        ) : (
                          <>
                            {domain.label}
                          </>
                        )}
                        {hasSubs && (
                          <ChevronDownIcon
                            className={`w-3 h-3 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                          />
                        )}
                      </Button>

                      {hasSubs && isExpanded && typeof document !== 'undefined' && createPortal(
                        <div
                          ref={dropdownRef}
                          data-testid="domain-dropdown"
                          className={`fixed z-[9999] min-w-max max-h-[calc(100vh-1.5rem)] overflow-y-auto bg-base-100 rounded-2xl shadow-lg border border-base-300 p-1 -translate-x-1/2 ${dropdownPosition ? '' : 'invisible'}`}
                          style={{
                            top: dropdownPosition?.top ?? 0,
                            left: dropdownPosition?.left ?? 0,
                          }}
                        >
                          <div
                            id={`domain-dropdown-${domain.id}`}
                            role="listbox"
                            aria-label={`${domain.label} sub-domains`}
                            className="flex flex-col gap-0.5"
                          >
                            {subs.map((sub) => {
                              const isSubActive = activeDomain?.id === sub.id;
                              const isArchiving = archivingSubId === sub.id;
                              return (
                                <div key={sub.id} className="group flex items-center gap-1">
                                  <Button
                                    role="option"
                                    aria-selected={isSubActive}
                                    className={`btn btn-xs min-h-0 h-7 !rounded-full border-0 shadow-none justify-start gap-1 flex-1 ${isSubActive ? 'btn-primary' : 'btn-ghost'}`}
                                    onClick={() => handleSubdomainClick(sub)}
                                  >
                                    {sub.label}
                                  </Button>
                                  {isAdminOrDev && !sub.protected && (
                                    <Button
                                      aria-label={`Remove ${sub.label}`}
                                      className="btn btn-ghost btn-xs btn-square min-h-0 h-7 w-7 !rounded-full text-base-content/40 hover:text-error opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                      onClick={(e) => handleArchiveSub(e, sub)}
                                      disabled={isArchiving}
                                    >
                                      {isArchiving
                                        ? <span className="loading loading-spinner loading-xs" />
                                        : <XMarkIcon className="w-3.5 h-3.5" aria-hidden="true" />
                                      }
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {schoolId && user?.uid && isAdminOrDev && (
                            <>
                              <div className="h-px bg-base-300 my-1" aria-hidden="true" />
                              <Button
                                className="btn btn-xs min-h-0 h-7 !rounded-full border-0 shadow-none justify-start gap-1 btn-ghost w-full"
                                onClick={() => handleCreateSubdomain(domain)}
                              >
                                <PlusIcon className="w-3.5 h-3.5" aria-hidden="true" />
                                Create sub-Domain
                              </Button>
                            </>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          </div>
        </div>

        <div className="shrink-0 bg-base-100 rounded-full shadow-sm border border-base-300 px-1.5 py-1">
          <PhaseScopeSelector />
        </div>
      </div>

      {archiveTarget && (() => {
        const { sub, count } = archiveTarget;
        const eventsLabel = sub.terminology?.event?.plural || 'records';
        const hasData = count > 0;
        const message = hasData
          ? `${count.toLocaleString()} ${eventsLabel} will be hidden — recoverable via support.`
          : `"${sub.label}" has no records yet.`;
        return (
          <ConfirmDialog
            isOpen
            onClose={() => setArchiveTarget(null)}
            onConfirm={performArchive}
            title={`Remove "${sub.label}"?`}
            message={message}
            confirmText="Remove"
            variant={hasData ? 'danger' : 'warning'}
            confirmTextMatch={hasData ? sub.label : null}
          />
        );
      })()}
    </div>
  );
};

export default PersistentContextBar;
